import React, { useState, useEffect, useContext, useCallback } from "react";
import { useForm } from "@mantine/form";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
  Alert,
  FormHelperText,
  Switch,
  FormControlLabel,
  Avatar,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import BusinessIcon from "@mui/icons-material/Business";
import AutoRepliesIcon from "@mui/icons-material/AutoMode";
import ConnectorsIcon from "@mui/icons-material/Extension";
import SaveIcon from "@mui/icons-material/Save";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DisabledByDefaultRounded from "@mui/icons-material/DisabledByDefaultRounded";
import ChatbotModeIcon from "@mui/icons-material/AccountTree";
import {
  getAllTemplates,
  getUserSettingsById,
  modifySettings,
} from "../../services/settings.service";
import { AgentCreationModel, AgentDetailModel } from "../../models/agent.model";
import {
  BusinessSetting,
  ConnectorSetting,
  MessageTemplate,
  UserSettingsAmendment,
} from "../../models/settings.model";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { STATIC_CONNECTORS } from "../../utilities/data.utility";
import useAuthUser from "../hooks/useAuthUser";
import WhatsAppTemplateTab from "./WhatsApp/WhatsappTemplateTab";
import { WhatsAppEmbeddedSignup } from "./WhatsApp/WhatsAppEmbeddedSignup";
import BusinessSettingsContext from "../Context/Settings/BusinessSettingsContext";
import LoadingSpinner from "../common/LoadingSpinner";
import ScheduledMessages from "../ScheduledMessages/ScheduledMessages";
import AutomationRulesConfig from "../AutomationRules/AutomationRulesConfig";
import BusinessReplyConfig from "../BusinessRepliesConfig/BusinessReplyConfig";
import { settingsSectionFromSearch } from "../dashboard/navigation";
import { DashboardHeading } from "../dashboard/DashboardPage";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import useSidebarPreference from "../dashboard/useSidebarPreference";
import { dashboardLayout } from "../dashboard/layoutTokens";
import CampaignLayout from "../Campaign";
import ChatbotModeSettings from "./ChatbotModeSettings";
import EcommWorkspace from "./EcommWorkspace";

// Country data interface
interface Country {
  code: string;
  name: string;
  flag: string;
  dialCode: string;
}

const TONE_OPTIONS = [
  { value: "polite", label: "Polite" },
  { value: "friendly", label: "Friendly" },
  { value: "professional", label: "Professional" },
];

// Fetch countries from GitHub
const fetchCountriesFromGitHub = async (): Promise<Country[]> => {
  try {
    const response = await fetch(
      "https://raw.githubusercontent.com/Resellfounders/tweetor-data/main/std-codes.json",
    );
    if (!response.ok) throw new Error("Failed to fetch countries");
    const data = await response.json();

    // Deduplicate by country code, keeping first occurrence
    const seenCodes = new Set<string>();
    const countries: Country[] = [];

    if (Array.isArray(data)) {
      for (const country of data) {
        if (country.code && !seenCodes.has(country.code)) {
          seenCodes.add(country.code);
          countries.push({
            code: country.code,
            name: country.name || "Unknown",
            flag: country.flag || "🏳️",
            dialCode: country.dialCode || "+1",
          });
        }
      }
    }

    return countries.length > 0 ? countries : [];
  } catch (error) {
    console.error("Failed to fetch countries, using defaults", error);
    // Fallback countries
    return [];
  }
};

// Agent Settings Tab - Redesigned Version
const AgentSettingsTab: React.FC<{
  settings: Partial<AgentDetailModel>;
  onSave: (settings: Partial<AgentDetailModel>) => void;
}> = ({ settings, onSave }) => {
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<Partial<AgentDetailModel>>({
    initialValues: settings,
    validate: {
      name: (value) =>
        !value || value.trim().length === 0 ? "Agent name is required" : null,
      instructions: (value) =>
        !value || value.trim().length === 0
          ? "Agent instructions are required"
          : null,
      fallbackResponse: (value) =>
        !value || value.trim().length === 0
          ? "Fallback response is required"
          : null,
      tones: (value) =>
        !value || value.length === 0 ? "Select at least one tone" : null,
    },
  });

  useEffect(() => {
    if (settings) {
      form.setValues({ ...settings });
    }
  }, [settings]);

  const handleSave = async () => {
    form.validate();
    if (Object.keys(form.errors).length === 0) {
      setIsSaving(true);
      await new Promise((resolve) => setTimeout(resolve, 500));
      onSave(form.values);
      setIsSaving(false);
    }
  };

  const toggleTone = (tone: string) => {
    const currentTones = form.values.tones;
    const newTones = currentTones?.includes(tone)
      ? currentTones?.filter((t: string) => t !== tone)
      : [...(currentTones ?? []), tone];
    form.setFieldValue("tones", newTones);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, m: { xs: 2, md: 3 }, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, maxWidth: dashboardLayout.formMaxWidth }}>
      <Stack spacing={3}>
        <Box>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ textAlign: "left" }}
            >
              Agent status
            </Typography>
            <FormControlLabel
              value={form.values.isActive}
              onChange={(e) => {
                console.log(e);
                form.setFieldValue("isActive", (e.target as any).checked);
              }}
              control={
                <Switch color="success" checked={form.values.isActive} />
              }
              label={
                <Typography
                  variant="button"
                  sx={{
                    color: form.values.isActive ? "success.main" : "text.secondary",
                  }}
                  fontWeight={700}
                >
                  {form.values.isActive ? "Active" : "Inactive"}
                </Typography>
              }
              labelPlacement="start"
            />
          </Stack>
        </Box>
        <Divider />
        <Box>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems={{ xs: "stretch", md: "flex-start" }}
          >
            {/* Agent Name Section */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 2, textAlign: "left" }}
              >
                Agent information
              </Typography>
              <TextField
                fullWidth
                label="Agent name"
                value={form.values.name}
                onChange={(e) => form.setFieldValue("name", e.target.value)}
                placeholder="e.g., Customer Support Agent"
                variant="outlined"
                size="small"
                error={!!form.errors.name}
                helperText={form.errors.name}
                required
              />
            </Box>

            {/* Status Section */}
            {/* <Box
              sx={{
                minWidth: { xs: "100%", md: 240 },
                maxWidth: { xs: "100%", md: 280 },
              }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 2, textAlign: "left" }}
              >
                Agent Status
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                }}
              >
                <Stack spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: form.values.isActive
                          ? "success.main"
                          : "error.main",
                        animation: form.values.isActive
                          ? "pulse 2s infinite"
                          : "none",
                        "@keyframes pulse": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0.5 },
                        },
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Current Status
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    fullWidth
                    size="small"
                    color="primary"
                    exclusive
                    value={form.values.isActive}
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        form.setFieldValue("status", newValue);
                      }
                    }}
                    aria-label="Agent Status"
                    sx={{
                      "& .MuiToggleButton-root": {
                        py: 1,
                        textTransform: "none",
                        fontWeight: 500,
                        "&.Mui-selected": {
                          fontWeight: 600,
                        },
                      },
                    }}
                  >
                    <ToggleButton value="ENABLE">
                      <CheckIcon sx={{ fontSize: 18, mr: 0.5 }} />
                      Active
                    </ToggleButton>
                    <ToggleButton value="DISABLE">
                      <XIcon sx={{ fontSize: 18, mr: 0.5 }} />
                      Disabled
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textAlign: "left", display: "block" }}
                  >
                    {form.values.isActive
                      ? "Agent is actively responding to messages"
                      : "Agent is paused and won't respond"}
                  </Typography>
                </Stack>
              </Paper>
            </Box> */}
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 2, textAlign: "left" }}
          >
            Agent Instructions
          </Typography>
          <TextField
            fullWidth
            label="Agent Instructions *"
            value={form.values.instructions}
            onChange={(e) => form.setFieldValue("instructions", e.target.value)}
            placeholder="Enter instructions for the agent..."
            variant="outlined"
            multiline
            rows={6}
            error={!!form.errors.instructions}
            helperText={
              form.errors.instructions ||
              "Define the behavior, tone, and guidelines for your agent"
            }
            required
          />
        </Box>

        <Divider />

        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 2, textAlign: "left" }}
          >
            Tone of Agent
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: "text.secondary", textAlign: "left" }}
          >
            Select one or more tones for your agent *
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            {TONE_OPTIONS.map((tone) => (
              <Chip
                key={tone.value}
                label={tone.label}
                onClick={() => toggleTone(tone.value)}
                color={
                  form.values.tones?.includes(tone.value)
                    ? "primary"
                    : "default"
                }
                variant={
                  form.values.tones?.includes(tone.value)
                    ? "filled"
                    : "outlined"
                }
              />
            ))}
          </Stack>
          {form.errors.tone && (
            <FormHelperText sx={{ color: "error.main", mt: 1 }}>
              {form.errors.tone}
            </FormHelperText>
          )}
        </Box>

        <Divider />

        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 2, textAlign: "left" }}
          >
            Fallback response
          </Typography>
          <TextField
            fullWidth
            label="Reply when the agent cannot answer"
            value={form.values.fallbackResponse}
            onChange={(e) =>
              form.setFieldValue("fallbackResponse", e.target.value)
            }
            placeholder="e.g., I'm not sure about that. Let me connect you with a representative..."
            variant="outlined"
            multiline
            rows={4}
            error={!!form.errors.fallbackResponse}
            helperText={form.errors.fallbackResponse}
            required
          />
        </Box>

        <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isSaving}
            sx={{ mt: 2 }}
          >
            {isSaving ? <CircularProgress size={20} /> : "Save Settings"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

// Business Settings Tab
const BusinessSettingsTab: React.FC<{
  onSave: (settings: BusinessSetting) => void;
  countries: Country[];
}> = ({ onSave, countries }) => {
  const { settings } = useContext(BusinessSettingsContext);
  const [isSaving, setIsSaving] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const country = countries.find((c) =>
      settings?.fullPhoneNumber?.startsWith(c.dialCode),
    );
    return country?.code || "US";
  });
  const [phoneNumber, setPhoneNumber] = useState(() => {
    const country = countries.find((c) =>
      settings?.fullPhoneNumber?.startsWith(c.dialCode),
    );
    return settings?.fullPhoneNumber
      ? settings.fullPhoneNumber.replace(country?.dialCode || "+1", "").trim()
      : "";
  });

  const form = useForm<BusinessSetting>({
    initialValues:
      settings ?? ({ name: "", fullPhoneNumber: "" } as BusinessSetting),
    validate: {
      name: (value: string) =>
        !value || value.trim().length === 0
          ? "Business name is required"
          : null,
      fullPhoneNumber: (value: string) => {
        if (!value || value.trim().length === 0) {
          return "Phone number is required";
        }
        if (!/^\+?[1-9]\d{7,14}$/.test(value)) {
          return "Phone number must include only numbers";
        }
      },
    },
  });

  const templateForm = useForm<{ name: string; content: string }>({
    initialValues: { name: "", content: "" },
    validate: {
      name: (value) =>
        !value || value.trim().length === 0
          ? "Template name is required"
          : null,
      content: (value) =>
        !value || value.trim().length === 0
          ? "Template content is required"
          : null,
    },
  });

  const handleSave = async () => {
    form.validate();
    if (Object.keys(form.errors).length === 0) {
      setIsSaving(true);
      await new Promise((resolve) => setTimeout(resolve, 500));

      onSave(form.values);
      setIsSaving(false);
    }
  };

  const handleAddTemplate = () => {
    templateForm.validate();
    if (Object.keys(templateForm.errors).length === 0) {
      const newTemplate = {
        id: `template_${Date.now()}`,
        name: templateForm.values.name,
        content: templateForm.values.content,
      };
      form.setFieldValue("messageTemplates", [
        ...form.values.messageTemplate,
        newTemplate,
      ]);
      templateForm.reset();
      setTemplateDialogOpen(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, m: { xs: 2, md: 3 }, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, maxWidth: dashboardLayout.formMaxWidth }}>
      <Stack spacing={3}>
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 2, textAlign: "left" }}
          >
            Connect with WhatsApp
          </Typography>
          <WhatsAppEmbeddedSignup />
        </Box>

        <Divider />

        <Box>
          <WhatsAppTemplateTab />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          ></Stack>

              {/* {form.values.messageTemplate?.length === 0 ? (
            <Paper sx={{ p: 2, bgcolor: "action.hover", textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No templates yet. Add one to get started.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              {form.values.messageTemplate?.map((template: MessageTemplate) => (
                <Card key={template.name} variant="outlined">
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {template.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, color: "text.secondary" }}
                        >
                          {template.value}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTemplate(template.name)}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )} */}
        </Box>
      </Stack>

      {/* Add Template Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Message Template</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Template Name *"
              value={templateForm.values.name}
              onChange={(e) =>
                templateForm.setFieldValue("name", e.target.value)
              }
              placeholder="e.g., Welcome Message"
              size="small"
              error={!!templateForm.errors.name}
              helperText={templateForm.errors.name}
              required
            />
            <TextField
              fullWidth
              label="Template Content *"
              value={templateForm.values.content}
              onChange={(e) =>
                templateForm.setFieldValue("content", e.target.value)
              }
              placeholder="Enter the message template..."
              multiline
              rows={4}
              error={!!templateForm.errors.content}
              helperText={templateForm.errors.content}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddTemplate}>
            Add Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Connectors Tab
const ConnectorsTab: React.FC<{
  settings: ConnectorSetting[];
  onSave: (settings: ConnectorSetting[]) => void;
}> = ({ settings, onSave }) => {
  const [connectors, setConnectors] =
    useState<ConnectorSetting[]>(STATIC_CONNECTORS);
  const [selectedConnector, setSelectedConnector] =
    useState<ConnectorSetting | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  const configForm = useForm<any>({
    initialValues: {
      shopLink: "",
      myShopifyLink: "",
      apiKey: "",
      isActive: false,
    },
    validate: {
      shopLink: (value) =>
        !value || value.trim().length === 0 ? "Shop link is required" : null,
      myShopifyLink: (value) =>
        !value || value.trim().length === 0
          ? "MyShopify link is required"
          : null,
      apiKey: (value) =>
        !value || value.trim().length === 0 ? "API key is required" : null,
    },
  });

  useEffect(() => {
    if (settings?.length) {
      const { connections } = settings[0];
      configForm.setValues({
        apiKey: connections["apiKey"],
        shopLink: connections["shopLink"],
        myShopifyLink: connections["myShopifyLink"],
        isActive: settings[0].isActive,
      });
      setSelectedConnector(settings[0]);

      setConnectors((existingConnectors: ConnectorSetting[]) => {
        if (existingConnectors) {
          const unMatchingConnectors = existingConnectors.filter(
            (connector: ConnectorSetting) =>
              connector.type !== settings[0].type,
          );

          return [...unMatchingConnectors, settings[0]];
        } else {
          return settings;
        }
      });
    }
  }, [settings]);

  const handleOpenConfig = (connector: ConnectorSetting) => {
    setSelectedConnector(connector);

    configForm.setValues({
      shopLink: connector.connections["shopLink"],
      myShopifyLink: connector.connections["myShopifyLink"],
      apiKey: connector.connections["apiKey"],
      isActive: connector.isActive,
    });
    setConfigDialogOpen(true);
  };

  const handleSaveConfig = () => {
    configForm.validate();
    if (Object.keys(configForm.errors).length === 0 && selectedConnector) {
      setConnectors((prev: ConnectorSetting[]) => {
        const data = prev.map((c: ConnectorSetting) =>
          c.type === selectedConnector.type
            ? {
                ...c,
                connections: configForm.values,
                isActive: configForm.values["isActive"],
                connectorName: STATIC_CONNECTORS[0].connectorName,
              }
            : c,
        );
        onSave(data);
        return data;
      });
      setConfigDialogOpen(false);
      setSelectedConnector(null);
    }
  };

  return (
    <Box sx={{ p: dashboardLayout.pagePadding, maxWidth: 1200 }}>
      <Stack spacing={3}>
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 1, textAlign: "left" }}
          >
            Available Connectors
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mb: 3, textAlign: "left" }}
          >
            Connect third-party services to extend your agent's capabilities
          </Typography>

          <Grid container spacing={3}>
            {connectors.map((connector) => (
              <Grid key={connector.connectorId} size={{ xs: 12, sm: 6, xl: 4 }}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor:
                      connector.type === "shopify" ? "not-allowed" : "pointer",
                    position: "relative",
                    transition: "all 0.3s ease",
                    opacity: connector.type === "shopify" ? 0.6 : 1,
                    pointerEvents:
                      connector.type === "shopify" ? "none" : "auto",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      display: "flex",
                      alignItems: "center",
                      height: 12,
                      width: 12,
                      bgcolor: connector.isActive
                        ? "success.light"
                        : "text.disabled",
                      borderRadius: "100%",
                      fontSize: "0.75rem",
                      fontWeight: 0,
                      color: "transparent",
                      zIndex: 1,
                    }}
                  >
                    <Tooltip
                      title={
                        connector.isActive
                          ? "Connector Active"
                          : "Connector Inactive"
                      }
                    >
                      <Typography>A</Typography>
                    </Tooltip>
                  </Box>

                  {/* Coming Soon Overlay */}
                  {connector.type === "shopify" && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "inherit",
                        zIndex: 10,
                        backdropFilter: "blur(1px)",
                      }}
                    >
                      <Stack alignItems="center" spacing={1}>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{
                            color: "#fff",
                            textAlign: "center",
                            textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                          }}
                        >
                          Coming Soon
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255,255,255,0.9)",
                            textAlign: "center",
                            fontSize: "0.75rem",
                          }}
                        >
                          We're working on this connector
                        </Typography>
                      </Stack>
                    </Box>
                  )}

                  <CardContent sx={{ flex: 1 }}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      sx={{ mb: 2 }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: "100%",
                          bgcolor: "action.hover",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {connector.type === "shopify" && (
                          <Avatar
                            src="https://img.icons8.com/?size=100&id=uSHYbs6PJfMT&format=png&color=000000"
                            sx={{ color: "primary.main" }}
                          />
                        )}
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{ textAlign: "left" }}
                        >
                          {connector.connectorName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textAlign: "left" }}
                        >
                          {connector.type === "shopify"
                            ? "E-commerce Platform"
                            : "Connector"}
                        </Typography>
                      </Box>
                    </Stack>

                    {connector.connections ? (
                      <Stack
                        spacing={2}
                        sx={{
                          bgcolor: "action.hover",
                          p: 1.5,
                          borderRadius: 1,
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          display="block"
                          sx={{ textAlign: "left" }}
                        >
                          Configuration{" "}
                          {connector.isActive ? "Active" : "Inactive"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textAlign: "left" }}
                        >
                          Shop: {connector.connections["shopLink"]}
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack
                        spacing={2}
                        sx={{
                          bgcolor: "action.hover",
                          p: 1.5,
                          borderRadius: 1,
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textAlign: "left" }}
                        >
                          Not configured
                        </Typography>
                      </Stack>
                    )}

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, textAlign: "left" }}
                    >
                      Integrate your {connector.connectorName} store to sync
                      products and orders with your agent.
                    </Typography>
                  </CardContent>

                  <Divider />

                  <Box sx={{ p: 2, display: "flex", gap: 1 }}>
                    <Button
                      fullWidth
                      variant={connector.isActive ? "outlined" : "contained"}
                      size="small"
                      onClick={() => handleOpenConfig(connector)}
                    >
                      Configure
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>

      {/* Configuration Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Configure {selectedConnector?.connectorName}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Shop Link *"
              value={configForm.values.shopLink}
              onChange={(e) =>
                configForm.setFieldValue("shopLink", e.target.value)
              }
              placeholder="e.g., https://your-store.shopify.com"
              size="small"
              error={!!configForm.errors.shopLink}
              helperText={configForm.errors.shopLink}
              required
            />
            <TextField
              fullWidth
              label="MyShopify Link *"
              value={configForm.values.myShopifyLink}
              onChange={(e) =>
                configForm.setFieldValue("myShopifyLink", e.target.value)
              }
              placeholder="e.g., your-store.myshopify.com"
              size="small"
              error={!!configForm.errors.myShopifyLink}
              helperText={configForm.errors.myShopifyLink}
              required
            />
            <TextField
              fullWidth
              label="API Key *"
              type="password"
              value={configForm.values.apiKey}
              onChange={(e) =>
                configForm.setFieldValue("apiKey", e.target.value)
              }
              placeholder="Enter your Shopify API key"
              size="small"
              error={!!configForm.errors.apiKey}
              helperText={configForm.errors.apiKey}
              required
            />

            <FormControlLabel
              control={
                <Switch
                  color="success"
                  checked={configForm.values.isActive}
                  onChange={(e) => {
                    configForm.setFieldValue("isActive", e.target.checked);
                  }}
                />
              }
              label="Activate"
            />
            <Alert severity="info">
              Your API credentials are securely encrypted and never shared.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveConfig}>
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const menuItems = [
  { id: 0, label: "Business settings", icon: BusinessIcon, description: "Connect WhatsApp and manage your business profile and message templates." },
  { id: 1, label: "Agent settings", icon: SettingsIcon, description: "Set your agent name, instructions, tone and fallback response." },
  { id: 2, label: "Chatbot mode", icon: ChatbotModeIcon, description: "Choose how your business handles conversations and hands them to your team." },
  { id: 3, label: "Connectors", icon: ConnectorsIcon, description: "Connect the services your agents use to answer questions and take action." },
  { id: 4, label: "Scheduled messages", icon: ScheduleIcon, description: "Schedule customer messages and review their delivery status." },
  { id: 5, label: "Auto replies", icon: AutoRepliesIcon, description: "Create and test matching rules for common customer questions." },
  { id: 6, label: "AI reply controls", icon: DisabledByDefaultRounded, description: "Choose when the agent replies and when your team handles the conversation." },
];

// Main Settings Interface
export default function SettingsInterface({ workspace = "settings" }: { workspace?: "settings" | "campaigns" }) {
  const { user } = useAuthUser();
  const [settingsCollapsed, toggleSettingsSidebar] = useSidebarPreference("settings");
  const settingsSidebarWidth = settingsCollapsed ? dashboardLayout.collapsedSidebarWidth : dashboardLayout.settingsSidebarWidth;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = workspace === "campaigns" ? 7 : settingsSectionFromSearch(searchParams.toString());
  const isSettingsSection = activeTab < 7;
  const currentSection = menuItems.find(item => item.id === activeTab) || menuItems[0];
  const [loadError, setLoadError] = useState("");
  const settingsHref = (id: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", String(id));
    return `/settings?${next.toString()}`;
  };
  const selectSettingsTab = (id: number) => setSearchParams(previous => {
    const next = new URLSearchParams(previous);
    next.set("tab", String(id));
    return next;
  });
  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [existingSettingsId, setExistingSettingsId] = useState<
    string | null | undefined
  >(null);
  const [agentSettings, setAgentSettings] = useState<Partial<AgentDetailModel>>(
    {
      name: "Default Agent",
      fallbackResponse: "I'm not sure about that. Let me help you further.",
      instructions: "",
      tones: ["professional"],
    },
  );
  const [businessSettings, setBusinessSettings] = useState<BusinessSetting>({
    fullPhoneNumber: "",
    name: "",
    messageTemplate: [],
    phoneNumberId: "",
    businessId: "",
  });

  const [connectorSettings, setConnectorSettings] =
    useState<ConnectorSetting[]>();

  const onSave = async (
    settingsType: "businessSettings" | "agentSettings" | "connectorSettings",
    settings: Partial<AgentDetailModel> | ConnectorSetting[] | BusinessSetting,
  ) => {
    const settingsToUpdate = {
      agentSettings,
      connectorSettings,
      businessSettings,
      [settingsType]:
        settingsType === "connectorSettings" ? settings : { ...settings },
    } as unknown as UserSettingsAmendment;

    try {
      await modifySettings(existingSettingsId, settingsToUpdate);
      console.log("Settings updated");

      fetchExistingSettings();
    } catch (e) {
      alert("Unable to update settings");
    }
  };

  const fetchExistingSettings = useCallback(async () => {
    setLoadError("");
    try {
      if (user?.uid) {
        const response = await getUserSettingsById(user.uid);

        if (response !== null) {
          const { agentSettings, businessSettings, connectorSettings } =
            response;

          setAgentSettings({
            name: agentSettings.name,
            fallbackResponse: agentSettings.fallbackResponse,
            instructions: agentSettings.instructions,
            tones: agentSettings.tones,
            isActive: agentSettings.isActive,
          });

          setBusinessSettings({
            fullPhoneNumber: businessSettings?.fullPhoneNumber,
            name: businessSettings?.name,
            messageTemplate: businessSettings?.messageTemplate,
            phoneNumberId: businessSettings?.phoneNumberId,
            businessId: businessSettings?.businessId,
          });

          setConnectorSettings(connectorSettings);

          setExistingSettingsId(user.uid);
        } else {
          throw Error("User not logged in for fetching settings");
        }
      } else {
        setExistingSettingsId(null);
        throw new Error("Unable to get response for user settings");
      }
    } catch (e) {
      setExistingSettingsId(null);
      setLoadError("We could not load your business settings. Check your connection and try again.");
    }
  }, [user?.uid]);

  const loadInitialData = useCallback(async () => {
    setIsLoadingInitial(true);
    try {
      await Promise.all([
        fetchCountriesFromGitHub().then(setCountries),
        fetchExistingSettings(),
      ]);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [fetchExistingSettings]);

  useEffect(() => { void loadInitialData(); }, [loadInitialData]);

  const drawerContent = (
    <Box id="desktop-settings-navigation" component="nav" aria-label="Settings navigation" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Menu Items */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: settingsCollapsed ? "center" : "space-between", gap: 1, px: settingsCollapsed ? 1 : 2, minHeight: 64 }}>
        {!settingsCollapsed && <Typography variant="subtitle2" color="text.secondary">Settings</Typography>}
        <Tooltip title={settingsCollapsed ? "Expand settings navigation" : "Collapse settings navigation"} placement="right">
          <IconButton aria-label={settingsCollapsed ? "Expand settings navigation" : "Collapse settings navigation"} aria-expanded={!settingsCollapsed} aria-controls="desktop-settings-navigation" onClick={toggleSettingsSidebar}>
            {settingsCollapsed ? <PanelLeftOpen size={19} aria-hidden="true" /> : <PanelLeftClose size={19} aria-hidden="true" />}
          </IconButton>
        </Tooltip>
      </Box>
      <List aria-label="Settings sections" sx={{ flex: 1, px: 1, pt: 0 }}>
        {menuItems.map((item) => {
          const IconComponent = item.icon;
          return (
            <ListItem key={item.id} disablePadding>
              <Tooltip title={settingsCollapsed ? item.label : ""} placement="right" arrow>
              <ListItemButton
                aria-label={settingsCollapsed ? item.label : undefined}
                selected={activeTab === item.id}
                aria-current={activeTab === item.id ? "page" : undefined}
                component={RouterLink}
                to={settingsHref(item.id)}
                sx={{
                  px: settingsCollapsed ? 1 : 1.5,
                  py: .8,
                  minHeight: 48,
                  justifyContent: settingsCollapsed ? "center" : "flex-start",
                  borderLeft: "3px solid",
                  borderColor:
                    activeTab === item.id ? "primary.main" : "transparent",
                  backgroundColor:
                    activeTab === item.id ? "action.selected" : "transparent",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: activeTab === item.id ? "primary.main" : "inherit",
                    minWidth: settingsCollapsed ? 0 : 30,
                    justifyContent: "center",
                    "& svg": { fontSize: 19 },
                  }}
                >
                  <IconComponent aria-hidden="true" />
                </ListItemIcon>
                {!settingsCollapsed && <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: activeTab === item.id ? 600 : 500,
                    fontSize: 12,
                  }}
                />}
              </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        flexDirection: { xs: "column", lg: "row" },
        backgroundColor: "background.default",
        position: "relative",
      }}
    >
      {isSettingsSection && <Box sx={{ display: { lg: "none" }, bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider", flexShrink: 0, minWidth: 0 }}>
        <Tabs value={activeTab} onChange={(_, id) => selectSettingsTab(id)} aria-label="Settings sections" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          {menuItems.map(item => <Tab key={item.id} value={item.id} label={item.label} id={`settings-tab-${item.id}`} aria-controls="settings-panel" />)}
        </Tabs>
      </Box>}

      {/* Secondary navigation belongs only to Settings. */}
      {isSettingsSection && <Box
        sx={{
          display: { xs: "none", lg: "block" },
          flexShrink: 0,
          width: settingsSidebarWidth,
          borderRight: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "action.disabled",
            borderRadius: "4px",
          },
        }}
      >
        {drawerContent}
      </Box>}

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "action.disabled",
            borderRadius: "4px",
          },
        }}
      >
        {isSettingsSection && <Box sx={{ p: dashboardLayout.pagePadding, pb: 0, "& > .MuiStack-root": { mb: 0 } }}>
          <DashboardHeading title={currentSection.label} description={currentSection.description} />
        </Box>}

        <Box id={isSettingsSection ? "settings-panel" : undefined} role={isSettingsSection ? "tabpanel" : undefined} aria-label={isSettingsSection ? currentSection.label : undefined} sx={{ flex: 1, minWidth: 0, "& > .MuiBox-root, & > .MuiStack-root": { minWidth: 0 } }}>
          {isLoadingInitial ? <Box role="status"><LoadingSpinner isLoading message="Loading workspace..." /></Box> : loadError ? <Box sx={{ p: dashboardLayout.pagePadding }}><Alert severity="error" action={<Button color="inherit" onClick={loadInitialData}>Try again</Button>}>{loadError}</Alert></Box> : <>
          {activeTab === 8 && <EcommWorkspace businessId={businessSettings.businessId} />}
          {activeTab === 0 && (
            <BusinessSettingsContext.Provider
              value={{
                settings: businessSettings,
                refreshSettings: (settingsId: string) =>
                  fetchExistingSettings(),
              }}
            >
              <BusinessSettingsTab
                onSave={(data) => onSave("businessSettings", data)}
                countries={countries}
              />
            </BusinessSettingsContext.Provider>
          )}
          {activeTab === 1 && (
            <AgentSettingsTab
              settings={agentSettings}
              onSave={(data) => onSave("agentSettings", data)}
            />
          )}
          {activeTab === 2 && (
            <ChatbotModeSettings businessId={businessSettings.businessId} />
          )}
          {activeTab === 3 && (
            <ConnectorsTab
              settings={connectorSettings ?? []}
              onSave={(data) => onSave("connectorSettings", data)}
            />
          )}
          {activeTab === 4 && (
            <BusinessSettingsContext.Provider
              value={{
                settings: businessSettings,
                refreshSettings: (settingsId: string) =>
                  fetchExistingSettings(),
              }}
            >
              <ScheduledMessages />
            </BusinessSettingsContext.Provider>
          )}
          {activeTab === 5 && (
            <BusinessSettingsContext.Provider
              value={{
                settings: businessSettings,
                refreshSettings: (settingsId: string) =>
                  fetchExistingSettings(),
              }}
            >
              <AutomationRulesConfig />
            </BusinessSettingsContext.Provider>
          )}
          {activeTab === 6 && (
            <BusinessSettingsContext.Provider
              value={{
                settings: businessSettings,
                refreshSettings: (settingsId: string) =>
                  fetchExistingSettings(),
              }}
            >
              <BusinessReplyConfig />
            </BusinessSettingsContext.Provider>
          )}
          {activeTab === 7 && (
            <BusinessSettingsContext.Provider
              value={{
                settings: businessSettings,
                refreshSettings: () => fetchExistingSettings(),
              }}
            >
              <CampaignLayout />
            </BusinessSettingsContext.Provider>
          )}
          </>}
        </Box>
      </Box>
    </Box>
  );
}
